import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import QuizIntroduction from "./QuizIntroduction";
import QuizQuestion from "./QuizQuestion";
import QuizEvaluation from "./QuizEvaluation";

const STATES = {
  introduction: "introduction",
  questions: "questions",
  evaluation: "evaluation",
};

const sendTrackingEvent = (points, resultCategory, isTest) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: isTest
        ? "climatequiz-finishedWithPoints-test"
        : "climatequiz-finishedWithPoints",
      points: points,
      pointsCategory: resultCategory,
      uuid: uuidv4(),
    });
  } else {
    console.log("No GTM dataLayer found, skip tracking.");
  }
};

const RESULT_CATEGORY = {
  champion: "champion",
  hero: "hero",
  grouch: "grouch",
};

const getResultCategory = (points, maxPoints) => {
  const percentage = points / maxPoints;

  if (percentage >= 0.8) {
    return RESULT_CATEGORY.champion;
  } else if (percentage < 0.8 && percentage >= 0.4) {
    return RESULT_CATEGORY.hero;
  } else {
    return RESULT_CATEGORY.grouch;
  }
};

const Quiz = ({ isTest, questions }) => {
  const [state, setState] = useState(STATES.introduction);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [tracked, setTracked] = useState(false);

  const addAnswer = (questionId, answerIds) => {
    const question = answers[questionId] ? answers[questionId] : {};

    setAnswers({
      ...answers,
      [questionId]: {
        completed: question.completed || false,
        answers: answerIds,
      },
    });
  };

  const setCompleted = (questionId) => {
    setAnswers({
      ...answers,
      [questionId]: {
        completed: true,
        answers: answers[questionId] && answers[questionId].answers,
      },
    });
  };

  switch (state) {
    case STATES.introduction:
      return <QuizIntroduction next={() => setState(STATES.questions)} />;

    case STATES.questions:
      const nextQuestion = currentQuestion + 1;
      const previousQuestion = currentQuestion - 1;
      const questionCount = questions.length;
      const question = questions[currentQuestion];
      const answer = answers[question.id] || {};

      return (
        <QuizQuestion
          question={question}
          next={() =>
            nextQuestion >= questionCount
              ? setState(STATES.evaluation)
              : setCurrentQuestion(nextQuestion)
          }
          previous={() =>
            previousQuestion < 0
              ? setState(STATES.introduction)
              : setCurrentQuestion(previousQuestion)
          }
          addAnswer={addAnswer}
          givenAnswers={answer.answers || []}
          completed={answer.completed}
          setCompleted={setCompleted}
        />
      );

    case STATES.evaluation:
      const SINGLE_ANSWER_POINTS = 3;
      const totalCount = questions.reduce((points, question) => {
        if (question.correctAnswers.length > 1) {
          return points + question.correctAnswers.length;
        } else {
          return points + SINGLE_ANSWER_POINTS;
        }
      }, 0);
      const correctAnswersCount = Object.entries(answers).reduce(
        (correctCount, [questionId, answer]) => {
          const question = questions.find(
            (question) => question.id === questionId
          );
          const correctAnswers = (answer.answers || [])
            .map((answerId) => question.correctAnswers.includes(answerId))
            .filter((correct) => correct === true).length;

          switch (question.type) {
            case "single":
              // Single answer questions give more points
              return correctCount + correctAnswers * SINGLE_ANSWER_POINTS;
            case "multiple":
              // Multiple choice questions give 1 point per correct answer
              return correctCount + correctAnswers;
            case "sort":
              const sortedCorrectAnswers = (answer.answers || [])
                .map((userAnswer, index) => {
                  if (question.correctAnswers[index] === userAnswer) {
                    return true;
                  } else {
                    return false;
                  }
                })
                .filter((correct) => correct === true).length;

              // Sorting questions give 1 point per correct answer
              return correctCount + sortedCorrectAnswers;
          }
        },
        0
      );

      if (!tracked) {
        sendTrackingEvent(
          correctAnswersCount,
          getResultCategory(correctAnswersCount, totalCount),
          isTest
        );
        setTracked(true);
      }

      return (
        <QuizEvaluation
          previous={() => setState(STATES.questions)}
          correctAnswersCount={correctAnswersCount}
          totalCount={totalCount}
          resultCategory={getResultCategory(correctAnswersCount, totalCount)}
        />
      );
  }
};

export default Quiz;
